<template>
    <section class="flex flex-col gap-4" :aria-label="t('personTeams.title')" :aria-busy="isFetching">
        <h2 ref="heading" tabindex="-1" class="title-section">{{ t("personTeams.title") }}</h2>
        <p class="body-small text-ink-muted empty:sr-only" role="status">
            <template v-if="isFetching">{{ t("personTeams.loading") }}</template>
        </p>
        <template v-if="!isFetching">
            <div v-if="error" class="flex flex-col items-start gap-3">
                <p role="alert" class="body-small text-ink">{{ t("personTeams.loadError") }}</p>
                <BaseButton theme="neutral" size="sm" @click="retry">{{ t("common.tryAgain") }}</BaseButton>
            </div>
            <ul v-else-if="data?.teams.length" class="flex flex-col gap-3">
                <li
                    v-for="team in data.teams"
                    :key="team.id"
                    ref="teamRows"
                    :data-team-id="team.id"
                    class="flex flex-wrap items-center justify-between gap-3 rounded-lgr border border-border bg-surface p-4">
                    <span class="body fs-mask text-ink">{{ team.name }}</span>
                    <div v-if="team.is_member && team.url" class="flex items-center gap-3">
                        <span class="body-small text-ink-muted" role="status">{{ t("personTeams.joined") }}</span>
                        <BaseLink :href="team.url" data-team-link class="body-small text-ink underline" :aria-label="t('personTeams.viewNamedTeam', { name: team.name })">
                            {{ t("personTeams.viewTeam") }}
                        </BaseLink>
                    </div>
                    <BaseButton
                        v-else-if="team.join_url"
                        theme="neutral"
                        size="sm"
                        :disabled="isJoining"
                        :aria-label="t('personTeams.joinNamedTeam', { name: team.name })"
                        @click="joinTeam(team)">
                        {{ isJoining && selectedTeamId === team.id ? t("personTeams.joining") : t("personTeams.join") }}
                    </BaseButton>
                    <p v-if="failedTeamId === team.id" role="alert" class="body-small text-ink w-full">{{ t("personTeams.joinError") }}</p>
                </li>
            </ul>
            <p v-else class="body-small text-ink-muted">{{ t("personTeams.empty") }}</p>
        </template>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { PersonTeam, PersonTeams } from "~vue/api/generated/team-perspective/types.gen";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ url: string }>();
const heading = useTemplateRef<HTMLElement>("heading");
const teamRows = useTemplateRef<HTMLElement[]>("teamRows");
const { t } = useI18n();
const { data, error, isFetching, execute, abort } = useFetch(props.url, { immediate: false }).get().json<PersonTeams>();
const joinUrl = ref("");
const selectedTeamId = ref<string | null>(null);
const failedTeamId = ref<string | null>(null);
const { data: joinedTeam, error: joinError, isFetching: isJoining, execute: join, abort: abortJoin } = useFetch(joinUrl, { immediate: false }).post().json<PersonTeam>();

async function retry() {
    heading.value?.focus();
    await execute();
}

async function joinTeam(team: PersonTeam) {
    if (isJoining.value || !team.join_url) {
        return;
    }
    const joinButton = teamRows.value?.find((element) => element.dataset.teamId === team.id)?.querySelector<HTMLButtonElement>("button");
    const restoreFocus = document.activeElement === joinButton;
    selectedTeamId.value = team.id;
    failedTeamId.value = null;
    joinUrl.value = team.join_url;
    await join();
    if (joinError.value || !joinedTeam.value) {
        failedTeamId.value = team.id;
        await nextTick();
        if (restoreFocus && document.activeElement === document.body) {
            joinButton?.focus();
        }
        return;
    }
    if (data.value) {
        data.value = { teams: data.value.teams.map((item) => (item.id === team.id ? joinedTeam.value! : item)) };
        await nextTick();
        const row = teamRows.value?.find((element) => element.dataset.teamId === team.id);
        if (document.activeElement === document.body || row?.contains(document.activeElement)) {
            row?.querySelector<HTMLElement>("[data-team-link]")?.focus();
        }
    }
}

onMounted(() => execute());
onBeforeUnmount(() => {
    abort();
    abortJoin();
});
</script>
